import React, { useState, useEffect } from 'react';
import { GlobalButton } from './GlobalButton';
import { GlobalInput } from './GlobalInput';
import { Toast } from './Toast';
import { useLocale } from '../context/DynamicLocaleProvider';
import { collection, addDoc } from 'firebase/firestore';
import styles from './ProjectModal.module.css';
import { db } from '../lib/firebase';

interface KnowledgeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onKnowledgeAdded: () => void;
}

export const KnowledgeModal: React.FC<KnowledgeModalProps> = ({
  isOpen,
  onClose,
  onKnowledgeAdded
}) => {
  const { t } = useLocale();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    knowledgeName: '',
    knowledgeDescription: ''
  });

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        knowledgeName: '',
        knowledgeDescription: ''
      });
      setError(null);
      setSuccess(null);
    }
  }, [isOpen]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.knowledgeName.trim()) {
      setError(t('knowledge_modal_error_required_fields') || 'Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await addDoc(collection(db, 'knowledge'), {
        knowledgeName: formData.knowledgeName,
        knowledgeDescription: formData.knowledgeDescription || ''
      });

      setSuccess(t('knowledge_modal_success') || 'Collection created successfully');

      setTimeout(() => {
        onClose();
        setFormData({
          knowledgeName: '',
          knowledgeDescription: ''
        });
        setLoading(false);
        setSuccess(null);
        onKnowledgeAdded();
      }, 1500);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Failed to create knowledge:', err);
      }
      setError(err instanceof Error ? err.message : t('knowledge_modal_error_create_failed') || 'Failed to create collection');
      setLoading(false);
    }
  };

  const clearError = () => setError(null);
  const clearSuccess = () => setSuccess(null);

  if (!isOpen) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2>{t('knowledge_modal_title') || 'Add collection'}</h2>
          <button onClick={onClose} className={styles.closeButton}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <GlobalInput
            label={t('knowledge_modal_name_label') || 'Collection Name'}
            value={formData.knowledgeName}
            onChange={(e) => handleInputChange('knowledgeName', e.target.value)}
            required
            placeholder={t('knowledge_modal_name_placeholder') || 'Enter collection name'}
          />

          <GlobalInput
            label={t('knowledge_modal_description_label') || 'Description'}
            value={formData.knowledgeDescription}
            onChange={(e) => handleInputChange('knowledgeDescription', e.target.value)}
            placeholder={t('knowledge_modal_description_placeholder') || 'Enter description (optional)'}
          />

          <div className={styles.actions}>
            <GlobalButton
              type="button"
              onClick={onClose}
              disabled={loading}
              variant="secondary"
            >
              {t('knowledge_modal_cancel_button') || 'Cancel'}
            </GlobalButton>
            <GlobalButton
              type="submit"
              disabled={loading || !formData.knowledgeName.trim()}
              isLoading={loading}
              variant="primary"
            >
              {t('knowledge_modal_create_button') || 'Create collection'}
            </GlobalButton>
          </div>
        </form>

        {error && (
          <Toast
            message={error}
            type="error"
            isVisible={true}
            onClose={clearError}
          />
        )}

        {success && (
          <Toast
            message={success}
            type="success"
            isVisible={true}
            onClose={clearSuccess}
          />
        )}
      </div>
    </div>
  );
};
